import { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../../api/client';
import StageRow from './StageRow';
import ColumnRow from './ColumnRow';
import ActionRow from './ActionRow';
import CustomDropdown from '../shared/CustomDropdown';

const NAVY = '#0C2D48';
const BORDER = '#e0e0e0';
const BG = '#f8f9fb';
const FONT = "'Raleway', sans-serif";

function uid() { return Math.random().toString(36).slice(2, 10); }

// ─── Style helpers ────────────────────────────────────────────────────────────

const s = {
  wrap: {
    fontFamily: FONT, color: NAVY,
  },
  section: {
    marginBottom: '1.5rem',
  },
  sectionHeader: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    marginBottom: '0.6rem',
  },
  sectionTitle: {
    fontSize: '0.78rem', fontWeight: 700, textTransform: 'uppercase',
    letterSpacing: '0.07em', color: '#888',
  },
  addBtn: {
    background: 'none', border: `1px solid ${BORDER}`, cursor: 'pointer',
    borderRadius: 8, padding: '0.25rem 0.7rem', fontSize: '0.78rem',
    color: '#6366f1', fontFamily: FONT, fontWeight: 600,
    transition: 'background 0.15s',
  },
  // Template selector
  tSelector: {
    marginBottom: '1.25rem',
  },
  tLabel: {
    fontSize: '0.78rem', fontWeight: 700, color: '#888', textTransform: 'uppercase',
    letterSpacing: '0.07em', marginBottom: '0.5rem', display: 'block',
  },
  // Actions panel
  actionsPanel: {
    background: '#f5f3ff', border: `1px solid #c7d2fe`,
    borderRadius: 8, padding: '0.75rem', marginBottom: 8, marginTop: -2,
  },
  actionsPanelHeader: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    marginBottom: '0.5rem',
  },
  actionsPanelTitle: {
    fontSize: '0.75rem', fontWeight: 700, color: '#6366f1', textTransform: 'uppercase',
    letterSpacing: '0.06em',
  },
  emptyMsg: {
    fontSize: '0.8rem', color: '#aaa', textAlign: 'center', padding: '0.75rem 0',
    fontStyle: 'italic',
  },
  loadingMsg: {
    fontSize: '0.8rem', color: '#aaa', padding: '1rem 0', textAlign: 'center',
  },
  readOnlyBanner: {
    background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8,
    padding: '0.5rem 0.75rem', fontSize: '0.78rem', color: '#92400e',
    marginBottom: '0.75rem',
  },
};

// ─── API path helpers ─────────────────────────────────────────────────────────

function basePath(mode, entityId) {
  if (mode === 'template')   return `/api/workflow-templates/${entityId}`;
  if (mode === 'project')    return `/api/projects/${entityId}`;
  if (mode === 'crm-board')  return `/api/crm-boards/${entityId}`;
  return null;
}

// ─── Template selector (create flow only) ────────────────────────────────────

const CUSTOM_TPL = { id: '__blank__', name: 'Custom', stages: [], columns: [], isSystem: true };

function TemplateSelector({ type, selected, onSelect }) {
  const [templates,      setTemplates]      = useState([]);
  const [open,           setOpen]           = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const ref = useRef(null);

  useEffect(() => {
    api(`/api/workflow-templates?type=${type}`)
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (d?.templates) {
          setTemplates(d.templates);
          const def = d.templates.find(t => t.isDefault) ?? d.templates[0];
          if (def) onSelect(def);
        }
      })
      .catch(() => {});
  }, [type]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    function outside(e) {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
        setConfirmDeleteId(null);
      }
    }
    document.addEventListener('mousedown', outside);
    return () => document.removeEventListener('mousedown', outside);
  }, []);

  const systemTemplates = templates.filter(t => t.isSystem);
  const userTemplates   = templates.filter(t => !t.isSystem);
  const selectedTemplate = [...templates, CUSTOM_TPL].find(t => t.id === selected);

  function stageHint(t) {
    return t.stages?.length ? `${t.stages.length} stage${t.stages.length !== 1 ? 's' : ''}` : 'No stages';
  }

  function selectTemplate(t) {
    onSelect(t);
    setOpen(false);
    setConfirmDeleteId(null);
  }

  async function handleDelete(tId, e) {
    e.stopPropagation();
    setConfirmDeleteId(tId);
  }

  async function confirmDelete(tId, e) {
    e.stopPropagation();
    try {
      await api(`/api/workflow-templates/${tId}`, { method: 'DELETE' });
      setTemplates(prev => prev.filter(t => t.id !== tId));
      if (selected === tId) {
        const remaining = templates.filter(t => t.id !== tId);
        const def = remaining.find(t => t.isDefault) ?? remaining[0];
        onSelect(def ?? CUSTOM_TPL);
      }
    } catch (_) {}
    setConfirmDeleteId(null);
  }

  const itemStyle = (isSelected) => ({
    padding: '9px 14px', cursor: 'pointer',
    background: isSelected ? `${NAVY}10` : '#fff',
    display: 'flex', alignItems: 'center',
  });

  return (
    <div ref={ref} style={{ ...s.tSelector, position: 'relative' }}>
      <span style={s.tLabel}>Template</span>
      <button
        type="button"
        onClick={() => { setOpen(v => !v); setConfirmDeleteId(null); }}
        style={{
          width: '100%', padding: '9px 12px',
          border: `1px solid ${BORDER}`, borderRadius: 8,
          fontSize: 14, boxSizing: 'border-box', outline: 'none',
          background: '#fff', fontFamily: FONT, color: '#111',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          cursor: 'pointer', textAlign: 'left',
        }}
      >
        <span style={{ fontWeight: 600 }}>{selectedTemplate?.name ?? 'Select a template…'}</span>
        <svg width="12" height="8" viewBox="0 0 12 8" fill="none"
          style={{ flexShrink: 0, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}>
          <path d="M1 1L6 7L11 1" stroke="#6b7280" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0,
          background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 8,
          boxShadow: '0 4px 16px rgba(0,0,0,0.12)', zIndex: 200, overflow: 'hidden',
        }}>
          {/* ── Aidey templates ── */}
          <div style={{ padding: '5px 14px 4px', fontSize: 10, fontWeight: 700, color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.07em', background: '#f9fafb', borderBottom: '1px solid #f0f0f0' }}>
            Aidey templates
          </div>
          {systemTemplates.map(t => (
            <div
              key={t.id}
              onClick={() => selectTemplate(t)}
              style={{ ...itemStyle(selected === t.id), borderBottom: '1px solid #f0f0f0' }}
              onMouseEnter={e => { if (selected !== t.id) e.currentTarget.style.background = '#f9fafb'; }}
              onMouseLeave={e => { e.currentTarget.style.background = selected === t.id ? `${NAVY}10` : '#fff'; }}
            >
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: selected === t.id ? NAVY : '#222' }}>{t.name}</div>
                <div style={{ fontSize: 11, color: '#888', marginTop: 1 }}>{stageHint(t)}</div>
              </div>
            </div>
          ))}

          {/* ── Your templates ── */}
          {userTemplates.length > 0 && (
            <>
              <div style={{ padding: '5px 14px 4px', fontSize: 10, fontWeight: 700, color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.07em', background: '#f9fafb', borderBottom: '1px solid #f0f0f0', borderTop: '1px solid #e5e7eb' }}>
                Your templates
              </div>
              {userTemplates.map(t => (
                <div key={t.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                  {confirmDeleteId === t.id ? (
                    <div style={{ padding: '8px 14px', background: '#fef2f2', display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}>
                      <span style={{ flex: 1, color: '#dc2626', fontFamily: FONT }}>Delete "{t.name}"? Won't affect existing projects.</span>
                      <button onClick={e => confirmDelete(t.id, e)} style={{ padding: '3px 10px', borderRadius: 5, border: 'none', background: '#dc2626', color: '#fff', cursor: 'pointer', fontSize: 12, fontWeight: 600, fontFamily: FONT }}>Delete</button>
                      <button onClick={e => { e.stopPropagation(); setConfirmDeleteId(null); }} style={{ padding: '3px 10px', borderRadius: 5, border: `1px solid ${BORDER}`, background: '#fff', color: '#666', cursor: 'pointer', fontSize: 12, fontFamily: FONT }}>Cancel</button>
                    </div>
                  ) : (
                    <div
                      onClick={() => selectTemplate(t)}
                      style={itemStyle(selected === t.id)}
                      onMouseEnter={e => { if (selected !== t.id) e.currentTarget.style.background = '#f9fafb'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = selected === t.id ? `${NAVY}10` : '#fff'; }}
                    >
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: selected === t.id ? NAVY : '#222' }}>{t.name}</div>
                        <div style={{ fontSize: 11, color: '#888', marginTop: 1 }}>{stageHint(t)}</div>
                      </div>
                      <button
                        onClick={e => handleDelete(t.id, e)}
                        title="Delete template"
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: 16, padding: '2px 4px', borderRadius: 3, lineHeight: 1, flexShrink: 0 }}
                      >
                        ×
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </>
          )}

          {/* ── Custom ── */}
          <div style={{ borderTop: '1px solid #e5e7eb' }}>
            <div
              onClick={() => selectTemplate(CUSTOM_TPL)}
              style={itemStyle(selected === '__blank__')}
              onMouseEnter={e => { if (selected !== '__blank__') e.currentTarget.style.background = '#f9fafb'; }}
              onMouseLeave={e => { e.currentTarget.style.background = selected === '__blank__' ? `${NAVY}10` : '#fff'; }}
            >
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: selected === '__blank__' ? NAVY : '#222' }}>Custom</div>
                <div style={{ fontSize: 11, color: '#888', marginTop: 1 }}>Define your own stages from scratch</div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function WorkflowEditor({
  mode = 'template',
  entityId = null,
  readOnly = false,
  showColumns = true,
  showAutomations = true,
  showSaveAsTemplate = false,
  onChange,
  onSaved,
}) {
  const [stages,   setStages]   = useState([]);
  const [columns,  setColumns]  = useState([]);
  // actionsMap: { [stageId]: StageAction[] }
  const [actionsMap, setActionsMap] = useState({});
  const [expandedStageId, setExpanded] = useState(null);
  const [loading,  setLoading]  = useState(!!entityId);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);

  // Save-as-template state
  const [saveTplOpen,    setSaveTplOpen]    = useState(false);
  const [saveTplName,    setSaveTplName]    = useState('');
  const [saveTplSaving,  setSaveTplSaving]  = useState(false);
  const [saveTplError,   setSaveTplError]   = useState('');
  const [saveTplSuccess, setSaveTplSuccess] = useState('');

  const isLive = !!entityId; // live mode: save to API immediately
  const supportsAutomations = showAutomations && mode === 'template';

  // ── Fetch on mount (live mode) ──────────────────────────────────────────────
  useEffect(() => {
    if (!entityId) return;
    setLoading(true);
    const base = basePath(mode, entityId);

    Promise.all([
      api(`${base}/stages`).then(r => r.ok ? r.json() : { stages: [] }),
      showColumns
        ? api(`${base}/columns`).then(r => r.ok ? r.json() : { columns: [] })
        : Promise.resolve({ columns: [] }),
    ]).then(([sd, cd]) => {
      const loadedStages = sd.stages || [];
      setStages(loadedStages);
      setColumns(cd.columns || []);

      // Fetch actions for each stage (template mode only)
      if (supportsAutomations && loadedStages.length) {
        Promise.all(
          loadedStages.map(st =>
            api(`${base}/stages/${st.id}/actions`)
              .then(r => r.ok ? r.json() : { actions: [] })
              .then(d => [st.id, d.actions || []])
          )
        ).then(pairs => {
          setActionsMap(Object.fromEntries(pairs));
        });
      }
    }).catch(err => {
      console.error('[WorkflowEditor] fetch failed', err);
    }).finally(() => setLoading(false));
  }, [entityId, mode, showColumns, supportsAutomations]);

  // ── Emit to parent (create mode) ───────────────────────────────────────────
  const emitChange = useCallback((newStages, newColumns) => {
    if (!isLive && onChange) onChange({ stages: newStages, columns: newColumns });
  }, [isLive, onChange]);

  // ─── Template pre-populate (create mode) ──────────────────────────────────
  function handleTemplateSelect(template) {
    if (template.id === '__blank__') {
      setStages([]);
      setColumns([]);
      setSelectedTemplateId('__blank__');
      emitChange([], []);
      return;
    }
    const newStages  = (template.stages  || []).map(s => ({ ...s, _localId: uid() }));
    const newColumns = (template.columns || []).map(c => ({ ...c, _localId: uid() }));
    setStages(newStages);
    setColumns(newColumns);
    setSelectedTemplateId(template.id);
    emitChange(newStages, newColumns);
  }

  // ─── Stage operations ──────────────────────────────────────────────────────

  async function addStage() {
    const newStage = { name: 'New stage', colour: '#94a3b8', position: stages.length, isTerminal: false, isStart: stages.length === 0 };
    if (isLive) {
      const base = basePath(mode, entityId);
      const r = await api(`${base}/stages`, { method: 'POST', body: JSON.stringify(newStage) });
      const d = await r.json();
      setStages(prev => [...prev, d.stage]);
      if (supportsAutomations) setActionsMap(prev => ({ ...prev, [d.stage.id]: [] }));
    } else {
      const local = { ...newStage, _localId: uid() };
      const next = [...stages, local];
      setStages(next);
      emitChange(next, columns);
    }
  }

  async function updateStage(idOrLocalId, patch) {
    if (isLive) {
      const stage = stages.find(s => s.id === idOrLocalId);
      if (!stage) return;
      const base = basePath(mode, entityId);
      await api(`${base}/stages/${stage.id}`, { method: 'PUT', body: JSON.stringify(patch) });
    }
    // Enforce single isStart
    let next = stages.map(s => {
      const key = isLive ? 'id' : '_localId';
      if (s[key] !== idOrLocalId) {
        if (patch.isStart) return { ...s, isStart: false };
        return s;
      }
      return { ...s, ...patch };
    });
    setStages(next);
    if (!isLive) emitChange(next, columns);
  }

  async function deleteStage(idOrLocalId) {
    if (stages.length <= 1) return;
    if (isLive) {
      const stage = stages.find(s => s.id === idOrLocalId);
      if (!stage) return;
      const base = basePath(mode, entityId);
      await api(`${base}/stages/${stage.id}`, { method: 'DELETE' });
      setActionsMap(prev => { const n = { ...prev }; delete n[stage.id]; return n; });
    }
    const next = stages.filter(s => (isLive ? s.id : s._localId) !== idOrLocalId);
    setStages(next);
    if (expandedStageId === idOrLocalId) setExpanded(null);
    if (!isLive) emitChange(next, columns);
  }

  async function moveStage(fromIdx, toIdx) {
    if (toIdx < 0 || toIdx >= stages.length) return;
    const next = [...stages];
    const [moved] = next.splice(fromIdx, 1);
    next.splice(toIdx, 0, moved);
    // Assign new positions
    const positioned = next.map((s, i) => ({ ...s, position: i }));
    setStages(positioned);

    if (isLive) {
      const base = basePath(mode, entityId);
      const order = positioned.map(s => ({ id: s.id, position: s.position }));
      await api(`${base}/stages/reorder`, { method: 'POST', body: JSON.stringify({ order }) });
    } else {
      emitChange(positioned, columns);
    }
  }

  // ─── Column operations ────────────────────────────────────────────────────

  async function addColumn() {
    const newCol = { name: 'New column', fieldKey: `col_${uid()}`, fieldType: 'TEXT', position: columns.length, isVisible: true, isRequired: false };
    if (isLive) {
      const base = basePath(mode, entityId);
      const r = await api(`${base}/columns`, { method: 'POST', body: JSON.stringify(newCol) });
      const d = await r.json();
      setColumns(prev => [...prev, d.column]);
    } else {
      const local = { ...newCol, _localId: uid() };
      const next = [...columns, local];
      setColumns(next);
      emitChange(stages, next);
    }
  }

  async function updateColumn(idOrLocalId, patch) {
    if (isLive) {
      const col = columns.find(c => c.id === idOrLocalId);
      if (!col) return;
      const base = basePath(mode, entityId);
      await api(`${base}/columns/${col.id}`, { method: 'PUT', body: JSON.stringify(patch) });
    }
    const next = columns.map(c => (isLive ? c.id : c._localId) === idOrLocalId ? { ...c, ...patch } : c);
    setColumns(next);
    if (!isLive) emitChange(stages, next);
  }

  async function deleteColumn(idOrLocalId) {
    if (isLive) {
      const col = columns.find(c => c.id === idOrLocalId);
      if (!col) return;
      const base = basePath(mode, entityId);
      await api(`${base}/columns/${col.id}`, { method: 'DELETE' });
    }
    const next = columns.filter(c => (isLive ? c.id : c._localId) !== idOrLocalId);
    setColumns(next);
    if (!isLive) emitChange(stages, next);
  }

  // ─── Action operations (template mode, live only) ─────────────────────────

  function actionCountForStage(stageId) {
    return (actionsMap[stageId] || []).length;
  }

  async function addAction(stageId) {
    const newAction = { trigger: 'ENTER', actionType: 'NOTIFY_ASSIGNEE', config: {}, isActive: true };
    const base = basePath(mode, entityId);
    const r = await api(`${base}/stages/${stageId}/actions`, { method: 'POST', body: JSON.stringify(newAction) });
    const d = await r.json();
    if (d.action) {
      setActionsMap(prev => ({ ...prev, [stageId]: [...(prev[stageId] || []), d.action] }));
    }
  }

  function handleActionSave(stageId, localPlaceholderId, savedAction) {
    setActionsMap(prev => ({
      ...prev,
      [stageId]: (prev[stageId] || []).map(a => a._localId === localPlaceholderId ? savedAction : a),
    }));
  }

  function handleActionDelete(stageId, actionId) {
    setActionsMap(prev => ({
      ...prev,
      [stageId]: (prev[stageId] || []).filter(a => a.id !== actionId),
    }));
  }

  // ─── Save as template ─────────────────────────────────────────────────────

  async function handleSaveAsTemplate() {
    if (!saveTplName.trim()) { setSaveTplError('Template name is required.'); return; }
    setSaveTplSaving(true);
    setSaveTplError('');
    const tplType = mode === 'crm-board' ? 'CRM' : 'PROJECT';
    try {
      const r = await api('/api/workflow-templates', {
        method: 'POST',
        body: JSON.stringify({
          name: saveTplName.trim(),
          type: tplType,
          isSystem: false,
          stages: stages.map((st, i) => ({
            name: st.name, colour: st.colour || '#94a3b8',
            position: i, isTerminal: st.isTerminal ?? false, isStart: st.isStart ?? false,
          })),
          columns: columns.map((c, i) => ({
            name: c.name, fieldKey: c.fieldKey,
            fieldType: c.fieldType || 'TEXT', options: c.options ?? null,
            position: i, isRequired: c.isRequired ?? false,
          })),
        }),
      });
      if (!r.ok) { const d = await r.json(); setSaveTplError(d.error || 'Save failed'); return; }
      setSaveTplOpen(false);
      setSaveTplName('');
      setSaveTplSuccess(
        tplType === 'CRM'
          ? 'Template saved — available when creating future boards'
          : 'Template saved — available when creating future projects'
      );
      setTimeout(() => setSaveTplSuccess(''), 4000);
    } catch (_) {
      setSaveTplError('Network error. Please try again.');
    } finally {
      setSaveTplSaving(false);
    }
  }

  // ─── Render ───────────────────────────────────────────────────────────────

  if (loading) return <div style={s.loadingMsg}>Loading workflow…</div>;

  const templateType = mode === 'crm-board' ? 'CRM' : 'PROJECT';

  return (
    <div style={s.wrap}>
      {/* Read-only banner for system templates */}
      {readOnly && (
        <div style={s.readOnlyBanner}>
          🔒 This is a system template and cannot be edited. Duplicate it to make changes.
        </div>
      )}

      {/* Template selector — create flow only */}
      {!isLive && (
        <div data-tour-id="workflow-template-selector">
          <TemplateSelector
            type={templateType}
            selected={selectedTemplateId}
            onSelect={handleTemplateSelect}
          />
        </div>
      )}

      {/* ── STAGES ── */}
      <div style={s.section}>
        <div data-tour-id="workflow-stages" style={s.sectionHeader}>
          <span style={s.sectionTitle}>Stages</span>
          {!readOnly && (
            <button data-tour-id="workflow-add-stage" style={s.addBtn} onClick={addStage}>+ Add stage</button>
          )}
        </div>

        {stages.length === 0 && (
          <div style={s.emptyMsg}>No stages yet. Add one or pick a template above.</div>
        )}

        {stages.map((stage, idx) => {
          const key    = isLive ? stage.id : (stage._localId || idx);
          const aCount = isLive ? actionCountForStage(stage.id) : 0;
          const isExpanded = expandedStageId === key;

          return (
            <div key={key}>
              <StageRow
                stage={stage}
                index={idx}
                total={stages.length}
                isOnlyStage={stages.length === 1}
                readOnly={readOnly}
                showAutomations={supportsAutomations}
                actionsCount={aCount}
                onMove={moveStage}
                onUpdate={patch => updateStage(key, patch)}
                onDelete={() => deleteStage(key)}
                onToggleActions={() => setExpanded(isExpanded ? null : key)}
              />

              {/* Actions panel */}
              {supportsAutomations && isExpanded && isLive && (
                <div data-tour-id={idx === 0 ? "workflow-automations" : undefined} style={s.actionsPanel}>
                  <div style={s.actionsPanelHeader}>
                    <span style={s.actionsPanelTitle}>⚡ Automations for "{stage.name}"</span>
                    {!readOnly && (
                      <button style={{ ...s.addBtn, fontSize: '0.75rem', padding: '0.2rem 0.5rem' }}
                        onClick={() => addAction(stage.id)}>
                        + Add rule
                      </button>
                    )}
                  </div>

                  {(actionsMap[stage.id] || []).length === 0 && (
                    <div style={{ ...s.emptyMsg, padding: '0.4rem 0' }}>
                      No automations for this stage yet.
                    </div>
                  )}

                  {(actionsMap[stage.id] || []).map((action, ai) => (
                    <ActionRow
                      key={action.id || action._localId || ai}
                      action={action}
                      stageId={stage.id}
                      templateId={entityId}
                      readOnly={readOnly}
                      onSave={saved => handleActionSave(stage.id, action._localId, saved)}
                      onDelete={() => handleActionDelete(stage.id, action.id)}
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* ── CUSTOM COLUMNS ── */}
      {showColumns && (
        <div style={s.section}>
          <div data-tour-id="workflow-columns" style={s.sectionHeader}>
            <span style={s.sectionTitle}>Custom columns</span>
            {!readOnly && (
              <button style={s.addBtn} onClick={addColumn}>+ Add column</button>
            )}
          </div>

          {columns.length === 0 && (
            <div style={s.emptyMsg}>No custom columns. Add fields to track extra data on each card.</div>
          )}

          {columns.map((col, idx) => {
            const key = isLive ? col.id : (col._localId || idx);
            return (
              <ColumnRow
                key={key}
                column={col}
                readOnly={readOnly}
                onChange={patch => updateColumn(key, patch)}
                onDelete={() => deleteColumn(key)}
              />
            );
          })}
        </div>
      )}

      {/* ── SAVE AS TEMPLATE ── */}
      {showSaveAsTemplate && isLive && !readOnly && (
        <div style={{ marginTop: '0.75rem', paddingTop: '1rem', borderTop: `1px solid ${BORDER}` }}>
          {saveTplSuccess && (
            <div style={{ padding: '8px 12px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 7, fontSize: 13, color: '#166534', marginBottom: 10, fontFamily: FONT, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span>✓ {saveTplSuccess}</span>
              <button
                onClick={() => window.dispatchEvent(new CustomEvent('aidey:goto-settings', { detail: 'workflow-templates' }))}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#166534', fontFamily: FONT, fontSize: 13, fontWeight: 700, padding: 0, textDecoration: 'underline' }}
              >
                Manage templates in Settings →
              </button>
            </div>
          )}
          {!saveTplOpen ? (
            <button
              data-tour-id="workflow-save"
              onClick={() => { setSaveTplOpen(true); setSaveTplError(''); setSaveTplSuccess(''); }}
              style={{ padding: '7px 14px', borderRadius: 7, border: `1px solid ${BORDER}`, background: '#fff', color: NAVY, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: FONT }}
            >
              💾 Save as template
            </button>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 400 }}>
              <input
                autoFocus
                value={saveTplName}
                onChange={e => setSaveTplName(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter')  handleSaveAsTemplate();
                  if (e.key === 'Escape') { setSaveTplOpen(false); setSaveTplName(''); setSaveTplError(''); }
                }}
                placeholder="e.g. My Sprint Workflow"
                style={{ padding: '8px 12px', border: `1px solid ${BORDER}`, borderRadius: 7, fontSize: 13, outline: 'none', fontFamily: FONT, color: '#111' }}
              />
              {saveTplError && <p style={{ margin: 0, color: '#dc2626', fontSize: 12, fontFamily: FONT }}>{saveTplError}</p>}
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  onClick={handleSaveAsTemplate}
                  disabled={saveTplSaving || !saveTplName.trim()}
                  style={{ padding: '7px 16px', borderRadius: 7, border: 'none', background: NAVY, color: '#fff', fontSize: 13, fontWeight: 700, cursor: saveTplSaving ? 'not-allowed' : 'pointer', fontFamily: FONT, opacity: (!saveTplName.trim() || saveTplSaving) ? 0.6 : 1 }}
                >
                  {saveTplSaving ? 'Saving…' : 'Save'}
                </button>
                <button
                  onClick={() => { setSaveTplOpen(false); setSaveTplName(''); setSaveTplError(''); }}
                  style={{ padding: '7px 14px', borderRadius: 7, border: `1px solid ${BORDER}`, background: '#fff', color: '#555', fontSize: 13, cursor: 'pointer', fontFamily: FONT }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
